/**
 * AI Message Input Component
 * For free-form messages when user selects "OTHER"
 * 
 * CRITICAL: Ensures ONE message is sent per request.
 * Prevents message batching, concatenation, or multiple submissions.
 */
import React, { useState, useRef } from 'react';
import { translations } from '../translations';

const AIMessageInput = ({ onSend, onRequestHuman, language, isLoading }) => {
  const [message, setMessage] = useState('');
  const isSubmittingRef = useRef(false); // Prevent multiple simultaneous submissions

  const t = translations[language] || translations.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // CRITICAL: Prevent multiple submissions
    if (isSubmittingRef.current || isLoading) {
      console.warn('Message submission already in progress, ignoring duplicate');
      return;
    }
    
    // Validate: message must be non-empty
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      return;
    }
    
    // CRITICAL: Clear input IMMEDIATELY (before async call)
    // This prevents the same message from being sent twice
    setMessage('');
    
    // CRITICAL: Set submitting flag BEFORE async call
    isSubmittingRef.current = true;
    
    try {
      // Send ONLY the current message (single message per request)
      await onSend(trimmedMessage);
    } catch (error) {
      console.error('Error sending message:', error);
      // Don't restore message on error - user can retype if needed
    } finally {
      // Reset submitting flag after completion
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className="afgcoin-chatbot-ai-input">
      <form 
        onSubmit={handleSubmit} 
        className="afgcoin-chatbot-message-form"
      >
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t.aiInput.placeholder}
          disabled={isLoading || isSubmittingRef.current}
          className="afgcoin-chatbot-message-input"
          onKeyDown={(e) => {
            // Prevent Enter key from submitting if already submitting
            if (e.key === 'Enter' && (isLoading || isSubmittingRef.current)) {
              e.preventDefault();
            }
          }}
        />
        <button
          type="submit"
          className="afgcoin-chatbot-send-button"
          disabled={isLoading || isSubmittingRef.current || !message.trim()}
        >
          {t.send}
        </button>
      </form>
      <button
        className="afgcoin-chatbot-human-button"
        onClick={onRequestHuman}
        disabled={isLoading}
      >
        {t.requestHuman}
      </button>
    </div>
  );
};

export default AIMessageInput;

